import React, { useState } from 'react';
import { X, User, Phone, Mail, MapPin, ChevronDown, Check, Loader2, ArrowRight } from 'lucide-react';

type Props = {
  open: boolean;
  setOpen: (value: boolean) => void;
  onSuccess?: () => void;
};

const ConsultFormModal = ({ open, setOpen, onSuccess }: Props) => {
  // --- States ---
  const [whatsappOptIn, setWhatsappOptIn] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  
  // Form Data State
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    city: ''
  });

  const [customCity, setCustomCity] = useState('');

  // Handle Input Change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle Form Submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const finalCity = formData.city === 'other' ? customCity : formData.city;

    const payload = {
        name: formData.name,
        phone: "+91" + formData.phone,
        email: formData.email,
        city: finalCity,
        whatsappOptIn: whatsappOptIn
    };

    try {
        const response = await fetch('https://luxuriyahomes.com/api/consultation/book', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });

        if (response.ok) {
            // Success hone par status change karo (Alert nahi)
            setSubmitStatus('success');
            
            // Optional: Agar parent component ko notify karna ho
            if (onSuccess) onSuccess();
            
            // Form Reset
            setFormData({ name: '', phone: '', email: '', city: '' });
            setCustomCity('');

        } else {
            const errorData = await response.text();
            setErrorMessage("Submission failed. Please try again.");
            setSubmitStatus('error');
        }
    } catch (error) {
        console.error("Connection Error:", error);
        setErrorMessage("Server not responding. Please check your internet.");
        setSubmitStatus('error');
    } finally {
        setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-stone-900/80 backdrop-blur-sm p-4 animate-fadeIn transition-opacity">
      
      {/* --- SPLIT LAYOUT MODAL --- */}
      <div className="bg-white rounded-2xl w-full max-w-3xl flex overflow-hidden shadow-2xl relative animate-scaleIn min-h-[500px]">
        
        {/* CLOSE BUTTON */}
        <button
          onClick={() => setOpen(false)}
          className="absolute top-4 right-4 z-20 text-gray-400 hover:text-black bg-white hover:bg-gray-100 rounded-full p-2 transition-all shadow-sm"
        >
          <X size={20} />
        </button>

        {/* --- LEFT SIDE: IMAGE --- */}
        <div className="hidden md:flex w-1/2 bg-stone-100 relative">
           <img 
             src="https://chiedesign.in/wp-content/uploads/2022/05/Luxury-Interior-Design-Living-Room.jpg" 
             alt="Luxury Interior"
             className="absolute inset-0 w-full h-full object-cover"
           />
           <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
           
           <div className="absolute bottom-8 left-8 text-white z-10">
              <h3 className="text-2xl font-serif font-bold mb-2">Design Your Dream</h3>
              <p className="text-sm text-gray-200 opacity-90 leading-relaxed">
                Get expert advice, 3D layouts, and a personalized quote.
              </p>
           </div>
        </div>

        {/* --- RIGHT SIDE: CONTENT AREA --- */}
        <div className="w-full md:w-1/2 p-8 md:p-10 flex flex-col justify-center bg-white relative">
          
          {/* --- VIEW 1: SUCCESS MESSAGE (Agar status success hai) --- */}
          {submitStatus === 'success' ? (
            <div className="flex flex-col items-center justify-center text-center h-full animate-fadeIn space-y-4">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-2">
                <Check className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-serif font-bold text-stone-800">Request Received!</h2>
              <p className="text-gray-500 text-sm leading-relaxed max-w-xs">
                Thank you for reaching out. Our design experts will contact you shortly on your provided number.
              </p>
              <button 
                onClick={() => setOpen(false)}
                className="mt-6 px-8 py-3 bg-stone-900 text-white text-xs font-bold uppercase tracking-widest hover:bg-amber-600 transition-colors rounded-sm"
              >
                Close Window
              </button>
            </div>
          ) : (
            
            /* --- VIEW 2: FORM (Agar status success nahi hai) --- */
            <>
              <div className="text-center md:text-left mb-6">
                <h2 className="text-sm font-bold text-amber-600 tracking-widest uppercase mb-2">
                  Book Appointment
                </h2>
                <h1 className="text-3xl font-serif font-bold text-stone-900">
                  Let's Talk Design
                </h1>
                <p className="text-gray-500 text-sm mt-2">
                  Fill the form below to meet our top designers.
                </p>
              </div>

              {/* Error Message Display */}
              {submitStatus === 'error' && (
                <div className="bg-red-50 text-red-600 text-xs p-3 rounded mb-4 border border-red-100">
                  {errorMessage}
                </div>
              )}

              <form className="space-y-4" onSubmit={handleSubmit}>
                {/* Inputs ... Same as before */}
                <div className="relative group">
                  <User className="absolute left-0 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 group-focus-within:text-amber-600 transition-colors" />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your Name"
                    required
                    className="w-full border-b border-gray-300 py-3 pl-8 text-sm focus:outline-none focus:border-amber-600 transition-colors placeholder:text-gray-400 text-gray-800"
                  />
                </div>

                <div className="relative group">
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 flex items-center gap-2 border-r border-gray-300 pr-2">
                     <span className="text-sm">🇮🇳</span>
                     <span className="text-xs font-bold text-gray-600">+91</span>
                  </div>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Phone Number"
                    required
                    pattern="[0-9]{10}"
                    className="w-full border-b border-gray-300 py-3 pl-[5.5rem] text-sm focus:outline-none focus:border-amber-600 transition-colors placeholder:text-gray-400 text-gray-800"
                  />
                </div>

                <div className="relative group">
                  <Mail className="absolute left-0 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 group-focus-within:text-amber-600 transition-colors" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Email Address"
                    required
                    className="w-full border-b border-gray-300 py-3 pl-8 text-sm focus:outline-none focus:border-amber-600 transition-colors placeholder:text-gray-400 text-gray-800"
                  />
                </div>

                {/* City Selection */}
                <div className="relative group">
                  <MapPin className="absolute left-0 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 group-focus-within:text-amber-600 transition-colors" />
                  <select 
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    className="w-full border-b border-gray-300 py-3 pl-8 text-sm text-gray-600 focus:outline-none focus:border-amber-600 transition-colors appearance-none bg-transparent cursor-pointer"
                  >
                    <option value="" disabled>Select Property City</option>
                    <option value="New Delhi">New Delhi</option>
                    <option value="Lucknow">Lucknow</option>
                    <option value="Varanasi">Varanasi</option>
                    <option value="Gurgaon">Gurgaon</option>
                    <option value="Noida">Noida</option>
                    <option value="other">Other</option>
                  </select>
                  <ChevronDown className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                </div>

                {formData.city === 'other' && (
                   <div className="relative group animate-fadeIn">
                     <MapPin className="absolute left-0 top-1/2 -translate-y-1/2 text-amber-600 w-5 h-5" />
                     <input
                       type="text"
                       placeholder="Please type your city name"
                       value={customCity}
                       onChange={(e) => setCustomCity(e.target.value)}
                       required
                       className="w-full border-b border-gray-300 py-3 pl-8 text-sm focus:outline-none focus:border-amber-600 transition-colors placeholder:text-gray-400 text-gray-800 bg-amber-50/50"
                     />
                   </div>
                )}

                <div className="flex items-center gap-2 pt-2">
                   <div 
                     onClick={() => setWhatsappOptIn(!whatsappOptIn)}
                     className={`w-5 h-5 rounded border cursor-pointer flex items-center justify-center transition-colors ${whatsappOptIn ? 'bg-green-600 border-green-600' : 'border-gray-300'}`}
                   >
                     {whatsappOptIn && <Check size={14} className="text-white" />}
                   </div>
                   <label className="text-xs text-gray-600 cursor-pointer" onClick={() => setWhatsappOptIn(!whatsappOptIn)}>
                     Send me updates on <span className="font-bold text-green-700">WhatsApp</span>
                   </label>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-stone-900 hover:bg-amber-600 disabled:bg-gray-400 text-white py-4 rounded-sm font-bold text-sm uppercase tracking-[0.2em] shadow-lg hover:shadow-xl transition-all duration-300 mt-4 flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="animate-spin w-4 h-4" /> Processing...
                    </>
                  ) : (
                    <>Get Free Quote <ArrowRight className="w-4 h-4" /></>
                  )}
                </button>

                <p className="text-[10px] text-gray-400 text-center mt-4">
                  By submitting, you agree to our <span className="underline cursor-pointer hover:text-amber-600">Privacy Policy</span>.
                </p>

              </form>
            </>
          )}

        </div>

      </div>
    </div>
  );
};

export default ConsultFormModal;